import { isArray, } from 'utils/array';
import { iterate, } from 'utils/object';
import { queryString, } from 'utils/window';
import { makeSafeFn, noop, } from 'utils/fn';
import { userStore, } from 'stores/userStore';
import { platform, viewport, domainFromFqdn, } from 'utils/ua';
import { getShortLocale, } from 'providers/i18nProvider/i18nProvider';
import { ADS_QUERYSTRING_OVERRIDE_PREFIXES, } from 'config/adsConfig';
const { info, groupCollapsed, groupEnd, } = require('application/console');
import { getCountryCode, NO_LOCATION, } from 'providers/geolocationProvider';
import { get as getFromPdo, ads as adsFromPdo, } from 'application/pageDataObject';
import { unprefixQsProperty, getPossiblyOverriddenPropByQsWithPrefix, } from 'utils/window';


const CUSTOM_CRITERIA_PREFIX = ADS_QUERYSTRING_OVERRIDE_PREFIXES.CUSTOM_CRITERIA;
const DEBUG_CC = queryString.adsDebugCc;
const DEFAULT_CC_VALUES = {
  AFFILIATE_ID: '0',
};
const getCcWithQsOverride = getPossiblyOverriddenPropByQsWithPrefix.part(CUSTOM_CRITERIA_PREFIX);
const DEBUG_CC_SPORTS_TYPES = queryString.adsDebugSportsTypes;
const SPORTS_TYPE_FOOTBALL = 'Soccer';
const SPORTS_TYPE_AMERICAN = 'American';
const SPORTS_TYPE_DEFAULT = SPORTS_TYPE_FOOTBALL;
const SPORTS_TYPE_BY_DOMAIN = {
  '12up': SPORTS_TYPE_AMERICAN,
};


function dfpTargetingValueToString(value){
  if (isArray(value)) {
    return value;
  }
  return value.toString();
}

function debugGroup(groupName){
  DEBUG_CC && groupCollapsed(`Custom Criteria: ${ groupName } ads`);
}

function debugGroupEnd(){
  DEBUG_CC && groupEnd();
}

function setTargetingViaTargetingFn(setTargetingFn, key, value){
  DEBUG_CC && info('%c[CC] %s:', 'font-style: italic; color: green;', key, value);
  makeSafeFn(setTargetingFn).invoke(key, value);
}

function setCustomCriteria(setTargetingFn, customCriteriaValue, customCriteriaName){
  const possiblyOverridenCcValue = getCcWithQsOverride(customCriteriaName, customCriteriaValue);
  if (possiblyOverridenCcValue) {
    setTargetingViaTargetingFn(setTargetingFn, customCriteriaName, possiblyOverridenCcValue);
  }
  return setTargetingFn;
}

function addCustomCriteria(setTargetingFn, additionalCustomCriteriaKey){
  return setCustomCriteria(setTargetingFn, undefined, additionalCustomCriteriaKey);
}

const removeCcPrefix = unprefixQsProperty.part(CUSTOM_CRITERIA_PREFIX);

function isUnknownCustomCriteria(qsKey, index, allQsKeys){
  // this function has to be bound to customCriteriaMap to work
  return qsKey.startsWith(CUSTOM_CRITERIA_PREFIX) && !(removeCcPrefix(qsKey) in this);
}

const DEFAULT_ENV = 'production';

function getAdditionalCustomCriteriaKeys(customCriteriaMap){
  return Object
    .keys(queryString)
    .filter(isUnknownCustomCriteria, customCriteriaMap)
    .map(removeCcPrefix);
}

function getLocaleNameForDfp(){
  return getShortLocale().toUpperCase();
}

let countryCode = NO_LOCATION;
function setCurrentCountryCode(countryCodeResult){
  countryCode = countryCodeResult;
}

getCountryCode()
  .then(setCurrentCountryCode)
  .catch(noop);

function getCustomCriteriaMapFromQueryString(qs){
  /* eslint-disable camelcase */
  const {
    a_aid: affiliateId = DEFAULT_CC_VALUES.AFFILIATE_ID,
    utm_medium: utmMedium,
    utm_source: utmSource,
    utm_campaign: utmCampaign,
    partnerId,
  } = qs;
  // eslint-enable
  return {
    affiliateId,
    partnerId,
    utmMedium,
    utmSource,
    utmCampaign,
  };
}

function getSportsTypesByDomain(domain){
  const singleSportsType = SPORTS_TYPE_BY_DOMAIN[domain] || SPORTS_TYPE_DEFAULT;
  DEBUG_CC_SPORTS_TYPES && info('[CC]\ncurrent domain: %s,\nsports-type: %s', domain, singleSportsType);
  return [singleSportsType, ];
}

function getPromotedMediaCc() {
  return getFromPdo('post.title', '').includes('VIDEO') ? 'video' : null;
}

function getBaseCustomCriteriaMap(){
  const { mainLevels, } = adsFromPdo.hierarchyLevels;
  const baseCc = {
    // FIXME: not just football any longer!
    sportsTypes: getSportsTypesByDomain(domainFromFqdn),
    language: getLocaleNameForDfp(),
    platform,
    articleId: getFromPdo('post.id'),
    articleTemplate: getFromPdo('post.template'),
    env: getFromPdo('config.env', DEFAULT_ENV),
    categories: getFromPdo('post.categories'),
    viewportOrientation: viewport.orientation,
    adUnitPath: mainLevels,
    countryCode,
    markupFormat: 'web',
    promotedMedia: getPromotedMediaCc(),
    // TODOS:
    // contentProvider: 'ESPN'
    // sizes: requested slot sizes ['970x250', '300x600', ]
    // screenSize: viewport breakpoint
  };
  return {
    ...baseCc,
    ...getCustomCriteriaMapFromQueryString(queryString),
    ...mainLevels,
  };
}

const pageVideoCustomCriteria = new Map();

function addVideoCcKeyValuePairToThis(ccValue, ccKey){
  const isCcValueStringable = ccValue !== undefined && ccValue.toString instanceof Function;
  const value = isCcValueStringable ? ccValue.toString() : '';
  if (value.length) {
    this.push(`${ ccKey }=${ value }`);
  }
  return this;
}

export function getVideoAdTargeting(instanceCustomCriteria = new Map()){
  if (pageVideoCustomCriteria.size === 0) {
    const setTargetingFn = pageVideoCustomCriteria.set.bind(pageVideoCustomCriteria);
    const setVideoCc = setCustomCriteria.part(setTargetingFn);
    debugGroup('video');
    const customCriteriaMap = getBaseCustomCriteriaMap();
    iterate(customCriteriaMap, setVideoCc);
    const additionalCustomCriteriaKeys = getAdditionalCustomCriteriaKeys(customCriteriaMap);
    const addVideoCc = addCustomCriteria.part(setTargetingFn);
    additionalCustomCriteriaKeys.forEach(addVideoCc);
    debugGroupEnd();
  }
  const videoCustomCriteria = [];
  // info(instanceCustomCriteria);
  pageVideoCustomCriteria.forEach(addVideoCcKeyValuePairToThis, videoCustomCriteria);
  instanceCustomCriteria.forEach(addVideoCcKeyValuePairToThis, videoCustomCriteria);
  // info(pageVideoCustomCriteria);
  // info(videoCustomCriteria);
  // Brightcove claim the delimiter should be ',' - when in fact we discovered via trial
  // and error, it doesn't. An ampersand does.
  return videoCustomCriteria.join('&');
}

export default function setPageTargeting(gTag, additionalCcMap = {}){
  const setTargetingFn = function getDfpSetTargeting(ccKey, ccValue){
    ccValue = dfpTargetingValueToString(ccValue); // eslint-disable-line no-param-reassign
    if (DEBUG_CC === 'verbose') {
      info(
        '%c[CC] %s:',
        'font-style: italic; color: blue;',
        `ccKey: '${ ccKey }', ccValue: '${ ccValue }'`
      );
    }
    return gTag.pubads().setTargeting(ccKey, ccValue);
  };
  const setGtagCc = setCustomCriteria.part(setTargetingFn);
  const customCriteriaMap = { ...getBaseCustomCriteriaMap(), ...additionalCcMap, };
  debugGroup('display');
  iterate(customCriteriaMap, setGtagCc);
  const additionalCustomCriteriaKeys = getAdditionalCustomCriteriaKeys(customCriteriaMap);
  const addGtagCc = addCustomCriteria.part(setTargetingFn);
  additionalCustomCriteriaKeys.forEach(addGtagCc);
  debugGroupEnd();
  return gTag;
}


// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/organizedByType/vms/ads/adTargeting.js